import { C } from "../constants/colors";

/* Cost by MODULE, with input and output kept apart.
 *
 * One blended cost-per-user number cannot answer the question that matters,
 * which is WHY a module is expensive. Input and output are priced five times
 * apart, and they mean opposite things: mostly output is Brill writing a lot,
 * which is the product working. Mostly input is the same context being re-sent
 * on every turn, which is waste. Measured 2026-08-24, `market` ran 46 calls at
 * ~76,000 input tokens each -- one pasted interview, billed forty-six times --
 * and that is invisible in any per-user total.
 *
 * So the bar is split, and `in/call` sits in the row: a big flat per-call input
 * number is the signature of a re-sent block.
 */

/* Shared with UsageByUser — one legend, one set of colours, so "in" means the
   same thing on both views. */
export const IN = C.salmon;      // input -- the one that means "re-sent context"
export const OUT = C.teal;       // output -- Brill actually writing
export const CACHE = C.amber;    // cache read + write

export const fmtCost = (v) => (v < 0.01 ? `$${v.toFixed(4)}` : `$${v.toFixed(2)}`);
export const fmtTok = (v) =>
  v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(v);

const TIER_LABEL = { accelerator: "Accelerator", momentum: "Momentum", other: "Open / other" };

export function Swatch({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "#777" }}>
      <span style={{ width: 9, height: 9, borderRadius: 2, background: color, flexShrink: 0 }} />
      {label}
    </span>
  );
}

export function UsageByModule({ modules = [], tiers = [] }) {
  if (!modules.length) {
    return <p style={{ fontSize: 13, color: "#bbb", textAlign: "center", padding: "40px 0" }}>No usage data yet.</p>;
  }

  const maxCost = Math.max(...modules.map((m) => m.cost), 0.0001);
  /* Per-call input, scaled against the heaviest module rather than an absolute
     number, so the outlier is obvious without needing to know what "normal" is. */
  const maxPerCall = Math.max(...modules.map((m) => m.inputPerCall), 1);

  return (
    <div>
      {/* Tier totals -- what a stage of the product costs to run */}
      {tiers.length > 0 && (
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          {tiers.map((t) => (
            <div key={t.tier} style={{
              flex: "1 1 160px", background: "#fff", border: "1px solid #e8e8f0",
              borderRadius: 10, padding: "10px 12px",
            }}>
              <div style={{ fontSize: 11, color: "#999", fontWeight: 600, marginBottom: 3 }}>{TIER_LABEL[t.tier] || t.tier}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.indigo }}>{fmtCost(t.cost)}</div>
              <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>
                {t.calls} calls · {fmtCost(t.costPerCall)}/call
              </div>
              <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>
                in {fmtCost(t.inputCost)} · out {fmtCost(t.outputCost)}
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
        <Swatch color={IN} label="input" />
        <Swatch color={OUT} label="output" />
        <Swatch color={CACHE} label="cache" />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {modules.map((m) => {
          const total = m.cost || 0.0001;
          const pct = (v) => `${(v / total) * 100}%`;
          return (
            <div key={m.moduleKey} style={{ background: "#fff", border: "1px solid #e8e8f0", borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7, gap: 8 }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.indigo, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {m.moduleKey}
                  </span>
                  <span style={{ fontSize: 10, color: "#aaa", flexShrink: 0 }}>{TIER_LABEL[m.tier] || m.tier}</span>
                </div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexShrink: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.indigo }}>{fmtCost(m.cost)}</span>
                  <span style={{ fontSize: 11, color: "#999" }}>{m.calls} calls</span>
                </div>
              </div>

              {/* Cost bar, split by where the money went. Width is this module
                  against the dearest one; the segments are its own split. */}
              <div style={{ background: "#f0eff6", borderRadius: 4, height: 6, overflow: "hidden", marginBottom: 6 }}>
                <div style={{ display: "flex", height: "100%", width: `${(m.cost / maxCost) * 100}%` }}>
                  <div style={{ background: IN, width: pct(m.inputCost) }} />
                  <div style={{ background: OUT, width: pct(m.outputCost) }} />
                  <div style={{ background: CACHE, width: pct(m.cacheCost) }} />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", fontSize: 11, color: "#888" }}>
                <span>
                  in {fmtCost(m.inputCost)} ({fmtTok(m.inputTokens)}) · out {fmtCost(m.outputCost)} ({fmtTok(m.outputTokens)})
                  {m.cacheReadTokens + m.cacheWriteTokens > 0
                    ? ` · cache ${fmtCost(m.cacheCost)} (${fmtTok(m.cacheReadTokens)} read)`
                    : ""}
                </span>
                <span style={{ fontWeight: 600, color: m.inputPerCall >= maxPerCall * 0.5 ? IN : "#888" }}>
                  {fmtTok(m.inputPerCall)} in/call
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
